import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs';
import { AppInstalableService } from '../../../core/services/app-instalable.service';
import { IconoComponent } from '../icono/icono.component';
import { InstalarAppComponent } from './instalar-app.component';

const ESPERA_MS = 4000;

/**
 * Invitación a instalar la app en el celular (D-147): sube desde abajo a los pocos segundos de
 * entrar. "Ahora no" la oculta 24 horas en ese navegador; mientras tanto queda el botón
 * destacado arriba del menú. No sale con la app ya instalada ni en la guía de ejemplo.
 */
@Component({
  selector: 'app-invitar-instalar',
  imports: [IconoComponent, InstalarAppComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    .hoja { animation: subir 320ms cubic-bezier(0.2, 0.9, 0.3, 1.1) both; }
    @keyframes subir { from { transform: translateY(120%); } to { transform: translateY(0); } }
    @media (prefers-reduced-motion: reduce) { .hoja { animation: none; } }
  `,
  template: `
    @if (visible()) {
      <div
        class="hoja fixed inset-x-3 z-[60] mx-auto max-w-md rounded-3xl border border-niebla bg-superficie p-4 shadow-2xl lg:hidden"
        style="bottom: calc(4.5rem + env(safe-area-inset-bottom))"
        role="dialog"
        aria-labelledby="t-instalar"
      >
        <div class="flex items-start gap-3">
          <img src="icono-192.png?v=4" alt="" width="52" height="52" class="size-13 shrink-0 rounded-2xl" />
          <div class="min-w-0 flex-1">
            <p id="t-instalar" class="font-display text-lg font-bold leading-tight">Instala la app de Pedidos SHEIN</p>
            <p class="mt-1 text-sm text-tinta-suave">Ábrela con un toque desde tu pantalla de inicio y recibe avisos de ofertas y de tus pedidos.</p>
          </div>
          <button type="button" (click)="cerrar()" class="-mr-1 -mt-1 grid size-9 shrink-0 place-items-center rounded-full hover:bg-lila" aria-label="Cerrar">
            <app-icono nombre="cerrar" [tamano]="18" />
          </button>
        </div>
        <div class="mt-3 flex items-center justify-end gap-2">
          <button type="button" (click)="cerrar()" class="rounded-full px-4 py-2.5 text-sm font-semibold hover:bg-lila">Ahora no</button>
          <app-instalar-app estilo="principal" />
        </div>
      </div>
    }
  `,
})
export class InvitarInstalarComponent {
  private readonly app = inject(AppInstalableService);
  private readonly router = inject(Router);
  private readonly tiempo = signal(false);
  private readonly ruta = toSignal(
    this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      map(() => this.router.url),
    ),
    { initialValue: '' },
  );

  readonly visible = computed(
    () => this.tiempo() && this.app.esMovil && this.app.disponible() && !this.app.descartada() && !this.ruta().includes('/tutorial/') && !this.ruta().startsWith('/login'),
  );

  constructor() {
    setTimeout(() => this.tiempo.set(true), ESPERA_MS);
  }

  cerrar(): void {
    this.app.descartar();
  }
}
